import { Box, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { FontFamily } from '@common/theming/Fonts';
import currency from '@common/utility/currency';
import { formatDate } from '@common/utility/datetime';
import { MSEarning, MSEarningTotals, MSEarnings } from '@services/appsync/types';

import React, { useMemo } from 'react';
import { CURRENCY_OPTIONS } from '../../constants';
import { useStatementViewModel } from '../../models/StatementViewModel';

export const Earnings: React.FC = () => {
  const statementViewModel = useStatementViewModel();

  const earnings: MSEarnings = useMemo(() => {
    const earnings = statementViewModel.statement?.activity.earnings;
    if (!earnings)
      return {
        __typename: 'MSEarnings',
        items: [],
        totals: { __typename: 'MSEarningTotals', net_amount: '', rewards_applied: '' },
      };

    const items: MSEarning[] = [];

    earnings?.items?.forEach((earning) => {
      const { rewards_applied, net_amount } = earning;

      items.push({
        __typename: 'MSEarning',
        date: formatDate(new Date(`${earning.date}T00:00:00`), 'MMMM DD, YYYY'),
        net_amount: net_amount ? currency.format(net_amount, CURRENCY_OPTIONS) : '',
        rewards_applied: rewards_applied ? currency.format(rewards_applied, CURRENCY_OPTIONS) : undefined,
        subscription_offer_name: earning.subscription_offer_name,
        type: earning.type,
      });
    });

    const totals: MSEarningTotals = {
      __typename: 'MSEarningTotals',
      net_amount: currency.format(earnings.totals.net_amount, CURRENCY_OPTIONS),
      rewards_applied: currency.format(earnings.totals.rewards_applied, CURRENCY_OPTIONS),
    };
    return { __typename: 'MSEarnings', items, totals };
  }, [statementViewModel.statement?.activity.earnings]);

  if (!earnings) return null;

  return (
    <Box w="full">
      <TableContainer>
        <Table px={'0px'} layout="fixed">
          <Thead>
            <Tr>
              <Td
                pl={'0px'}
                borderBottomStyle="none"
                fontSize="16px"
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                colSpan={6}>
                Earnings
              </Td>
            </Tr>
            <Tr py="6px" borderBottom={'2px solid black'}>
              <Th
                className="statement-table-header"
                lineHeight={1}
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                pl={'0px'}
                color="black">
                Date
              </Th>
              <Th
                className="statement-table-header"
                lineHeight={1}
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                color="black">
                Source
              </Th>
              <Th
                className="statement-table-header"
                lineHeight={1}
                colSpan={2}
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                color="black">
                Type
              </Th>
              <Th
                className="statement-table-header"
                lineHeight={1}
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                color="black">
                Rewards Applied
              </Th>
              <Th
                className="statement-table-header"
                lineHeight={1}
                fontFamily={FontFamily.HELVETICA_NOW_BOLD}
                fontWeight="normal"
                color="black">
                Total Amount
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {earnings.items?.map((item, i) => (
              <Tr py="6px" key={`holding-${i}`}>
                <Td pl="4px">{item.date}</Td>
                <Td>{item.subscription_offer_name}</Td>
                <Td colSpan={2}>{item.type}</Td>
                <Td>{item.rewards_applied}</Td>
                <Td>{item.net_amount}</Td>
              </Tr>
            ))}
            <Tr py="6px" backgroundColor="gray.50">
              <Td pl="4px" colSpan={4} fontFamily={FontFamily.HELVETICA_NOW_BOLD}>
                Total
              </Td>
              <Td fontFamily={FontFamily.HELVETICA_NOW_BOLD}>{earnings.totals.rewards_applied}</Td>
              <Td fontFamily={FontFamily.HELVETICA_NOW_BOLD}>{earnings.totals.net_amount}</Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
